import React, { useState, useEffect } from "react";
import { Badge } from "@shared/ui/badge";
import { Check, AlertCircle, Clock, Loader2, Eye } from "lucide-react";
import { useWorkflowRuns } from "../../hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HistoryMessage } from "../../types";
import { PAGE_SIZE } from "@shared/utils/constants";

interface WorkflowHistoryProps {
  executionHistory?: WorkflowExecution[];
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  recipientCount: number;
  messages: HistoryMessage[];
  successRate?: number;
  deliveredCount?: number;
  failedCount?: number;
  pendingCount?: number;
  totalNotifications?: number;
  typeOf?: string;
}

// Define filters interface for workflow runs
interface WorkflowRunFilters {
  page: number;
  limit: number;
  remote_utility_id?: string;
  search_data?: string;
  statusDisplay?: string;
  type?: string;
}

// Map API response to component interface
const mapApiDataToWorkflowExecution = (apiData: any): WorkflowExecution => {
  return {
    id: apiData.id.toString(),
    workflowId: apiData.id?.toString(),
    workflowName: apiData.workflow
      ? `Workflow ${apiData.workflow}`
      : `${apiData.typeOf} Run ${apiData.id}`,
    startedAt: `${apiData.startDate} ${apiData.startedAt}`,
    completedAt: `${apiData.completeDate} ${apiData.completedAt}`,
    status: apiData.statusDisplay,
    recipientCount: apiData.recipientsCount,
    messages: [], // API doesn't include message details in this endpoint
    successRate: apiData.successRate,
    deliveredCount: apiData.deliveredCount,
    failedCount: apiData.failedCount,
    pendingCount: apiData.pendingCount,
    totalNotifications: apiData.totalNotifications,
    typeOf: apiData.typeOf,
  };
};

export const WorkflowHistory: React.FC<WorkflowHistoryProps> = ({}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = PAGE_SIZE;
  const navigate = useNavigate();

  const currentPage = parseInt(searchParams.get("page") || "1");
  const searchQuery = searchParams.get("query") || "";

  const { data: workflowRunsData } = useWorkflowRuns({
    page: currentPage,
    limit: itemsPerPage,
    search_data: searchQuery,
  });

  const processedExecutionHistory = React.useMemo(() => {
    if (workflowRunsData?.results) {
      return workflowRunsData.results.map(mapApiDataToWorkflowExecution);
    }
    return [];
  }, [workflowRunsData]);

  // Define table columns
  const columns: TableColumn<WorkflowExecution>[] = [
    {
      key: "workflowName",
      header: "Workflow",
      width: "w-64",
      type: "custom",
      render: (_: any, row: WorkflowExecution) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {row.workflowName}
          </div>
          <div className="text-xs text-gray-500">ID: {row.id}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-32",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          const lowerValue = value.toLowerCase();
          switch (lowerValue) {
            case "completed":
              return "default";
            case "failed":
              return "destructive";
            case "in progress":
              return "outline";
            default:
              return "secondary";
          }
        },
        getClassName: (value: string) => {
          const lowerValue = value.toLowerCase();
          switch (lowerValue) {
            case "completed":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            case "failed":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            case "in progress":
              return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "typeOf",
      header: "Type",
      width: "w-24",
      type: "custom",
      render: (_: any, row: WorkflowExecution) => (
        <span className="text-sm text-gray-900">{row.typeOf || "-"}</span>
      ),
    },
    {
      key: "recipientCount",
      header: "Recipients",
      width: "w-24",
      type: "custom",
      render: (_: any, row: WorkflowExecution) => (
        <span className="text-sm font-medium">{row.recipientCount}</span>
      ),
    },
    {
      key: "statistics",
      header: "Statistics",
      width: "w-48",
      type: "custom",
      sortable: false,
      render: (_: any, row: WorkflowExecution) => (
        <div className="text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-500">Total:</span>
            <span className="font-medium">{row.totalNotifications || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Delivered:</span>
            <span className="font-medium text-green-600">
              {row.deliveredCount || 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Failed:</span>
            <span className="font-medium text-red-600">
              {row.failedCount || 0}
            </span>
          </div>
          {row.successRate !== undefined && (
            <div className="flex justify-between">
              <span className="text-gray-500">Success:</span>
              <span className="font-medium">{row.successRate}%</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "startedAt",
      header: "Started",
      width: "w-32",
      type: "custom",
      render: (_: any, row: WorkflowExecution) => (
        <div className="text-sm">
          <div>{row.startedAt}</div>
        </div>
      ),
    },
    {
      key: "completedAt",
      header: "Completed",
      width: "w-32",
      type: "custom",
      render: (_: any, row: WorkflowExecution) => (
        <div className="text-sm">
          {row.completedAt ? (
            row.completedAt
          ) : (
            <span className="text-gray-500">In progress</span>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      width: "w-24",
      type: "custom",
      render: (_: any, row: WorkflowExecution) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="text-blue-600 hover:text-blue-900 transition-colors"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // Handle view details
  const handleViewDetails = (execution: WorkflowExecution) => {
    console.log("View details for:", execution);
    // Navigate to the details page
    navigate(`/communication-hub/workflows/history/${execution.id}`);
  };

  // Handle apply filters
  const handleApplyFilters = (newFilters: {
    statusDisplay?: string;
    type?: string;
  }) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("type");
    params.delete("page");

    // Set new filters
    if (newFilters.statusDisplay && newFilters.statusDisplay !== "ALL_STATUS") {
      params.set("status", newFilters.statusDisplay);
    }

    if (newFilters.type && newFilters.type !== "ALL_TYPES") {
      params.set("type", newFilters.type);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("type");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle search
  const handleSearch = (searchTerm: string, paramName: string) => {
    console.log(`Search term: "${searchTerm}", Param: "${paramName}"`);

    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setSearchParams(params);
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Workflow Execution History
        </h2>
        <p className="text-sm text-gray-600">
          View and manage all workflow execution history
        </p>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={processedExecutionHistory || []}
        columns={columns}
        searchPlaceholder="Search workflow executions..."
        searchParamName="query" // This will be mapped to search_data in API
        searchDebounceMs={300}
        onSearch={handleSearch}
        rowKey="id"
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((workflowRunsData?.count || 0) / itemsPerPage),
          totalItems: workflowRunsData?.count || 0,
          itemsPerPage,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(
            currentPage * itemsPerPage,
            workflowRunsData?.count || 0
          ),
        }}
        onPageChange={handlePageChange}
        emptyMessage="No workflow executions found"
      />
    </div>
  );
};

// Filter Panel Component
interface WorkflowFilterPanelProps {
  currentFilters: WorkflowRunFilters;
  onApplyFilters: (filters: Partial<WorkflowRunFilters>) => void;
  onResetFilters: () => void;
}

const WorkflowFilterPanel: React.FC<WorkflowFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const [localFilters, setLocalFilters] = useState({
    status: currentFilters.statusDisplay || "ALL_STATUS",
    type: currentFilters.type || "ALL_TYPES",
  });

  // Update local filters when currentFilters change
  useEffect(() => {
    setLocalFilters({
      status: currentFilters.statusDisplay || "ALL_STATUS",
      type: currentFilters.type || "ALL_TYPES",
    });
  }, [currentFilters]);

  const handleApply = () => {
    const filtersToApply: Partial<WorkflowRunFilters> = {};

    if (localFilters.status && localFilters.status !== "ALL_STATUS") {
      filtersToApply.statusDisplay = localFilters.status;
    }

    if (localFilters.type && localFilters.type !== "ALL_TYPES") {
      filtersToApply.type = localFilters.type;
    }

    onApplyFilters(filtersToApply);
  };

  const handleReset = () => {
    setLocalFilters({
      status: "ALL_STATUS",
      type: "ALL_TYPES",
    });
    onResetFilters();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
      <div className="space-y-2">
        <label className="text-sm font-medium">Status</label>
        <select
          value={localFilters.status}
          onChange={(e) =>
            setLocalFilters((prev) => ({ ...prev, status: e.target.value }))
          }
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="ALL_STATUS">All Statuses</option>
          <option value="Completed">Completed</option>
          <option value="In Progress">In Progress</option>
          <option value="Failed">Failed</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Type</label>
        <select
          value={localFilters.type}
          onChange={(e) =>
            setLocalFilters((prev) => ({ ...prev, type: e.target.value }))
          }
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="ALL_TYPES">All Types</option>
          <option value="Bulk">Bulk</option>
          <option value="Single">Single</option>
        </select>
      </div>

      <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={handleReset}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
        >
          Reset Filters
        </button>
        <button
          onClick={handleApply}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};
